import { useEffect, useReducer, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { SessionTemplateErrorCode, type SessionTemplateDetail } from "shared"
import { useLocation } from "wouter"
import {
  fetchAdminPrograms,
  fetchServiceTypes,
  fetchSessionTemplate,
  updateSessionTemplate,
} from "../api"
import { AdminButton } from "@rawa/ui/AdminButton"
import { DiscardChangesDialog } from "../components/DiscardChangesDialog"
import { PlacedModuleCard } from "../components/PlacedModuleCard"
import {
  EmptyCanvas,
  SessionTemplateBuilder,
} from "../components/SessionTemplateBuilder"
import { SessionTemplateNameFields } from "../components/SessionTemplateNameFields"
import { SessionTemplatePreviewDialog } from "../components/SessionTemplatePreviewDialog"
import {
  canSave,
  hasUnsavedChanges,
  seedTemplateState,
  templateReducer,
  toUpdate,
  type TemplateContent,
} from "../stores/templateState"
import { TEMPLATE_NAMES, useNameTaken } from "../hooks/useNameTaken"
import { eventForDrop } from "../utils/templateDrag"
import EyeIcon from "../../../assets/icon-eye.svg?react"
import { MultiSelectField } from "@rawa/ui/MultiSelectField"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { SaveRefusal } from "../components/SaveRefusal"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import { paths } from "../../../paths"
import {
  ApiResponseError,
  isExpectedApiError,
  isForbidden,
} from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import styles from "./SessionTemplatePage.module.css"

/**
 * A template's edit screen. The editor is seeded once, so it waits for this
 * visit's own read rather than starting from a template an earlier visit left
 * in the cache: a module archived meanwhile would otherwise show as it was.
 */
export function SessionTemplatePage({ id }: { id: string }) {
  const {
    data: sessionTemplate,
    isFetchedAfterMount,
    isError,
    error,
  } = useQuery({
    queryKey: ["session-template", id],
    queryFn: ({ signal }) => fetchSessionTemplate(id, signal),
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchSessionTemplate", forbiddenIsExpected: true },
  })

  // A 403 is an authorization outcome with a screen of its own; anything else
  // is a failure to load.
  const forbidden = isError && isForbidden(error)
  const loadError = isError && !forbidden

  return (
    <div className={styles.page}>
      {!isFetchedAfterMount && !isError && (
        <div
          role="status"
          aria-label="Loading session template"
          className={styles.panel}
        >
          <span className={styles.skeletonName} />
        </div>
      )}

      {forbidden && <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>}

      {loadError && (
        <ErrorCallout>Failed to load session template.</ErrorCallout>
      )}

      {sessionTemplate && isFetchedAfterMount && (
        <SessionTemplateEditor
          key={sessionTemplate.id}
          sessionTemplate={sessionTemplate}
        />
      )}
    </div>
  )
}

/**
 * The builder for one loaded template: its details, the modules on its
 * canvas, and the library they are added from.
 *
 * The page mounts it keyed by template id, so its state is seeded from the
 * template exactly once, here, and another template gets a fresh editor. A
 * later read of the same template updates only what renders from the prop.
 */
function SessionTemplateEditor({
  sessionTemplate,
}: {
  sessionTemplate: SessionTemplateDetail
}) {
  // The administrator's working copy of the template.
  const [state, dispatch] = useReducer(
    templateReducer,
    sessionTemplate,
    seedTemplateState,
  )
  const { content } = state
  const queryClient = useQueryClient()
  const [, navigate] = useLocation()
  const [leaving, setLeaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)

  // A drop changes the template as the buttons would (see eventForDrop).
  useEffect(
    () =>
      monitorForElements({
        onDrop: ({ source, location }) => {
          const event = eventForDrop(
            content.modules,
            source.data,
            location.current.dropTargets[0]?.data,
          )
          if (event) dispatch(event)
        },
      }),
    [content.modules],
  )
  const cancelRef = useRef<HTMLButtonElement>(null)
  const changed = hasUnsavedChanges(state)
  const nameTaken = useNameTaken(
    TEMPLATE_NAMES,
    content.name,
    sessionTemplate.name,
  )

  // Only programmes ICIS still has active are offered, as when the template was
  // set up. A list that did not load is not a reason to hold the builder up;
  // the cache records the fault.
  const { data: programs = [] } = useQuery({
    queryKey: ["programs"],
    queryFn: ({ signal }) => fetchAdminPrograms(signal),
    select: (all) => all.filter((program) => program.active),
    retry: false,
    meta: { source: "fetchAdminPrograms" },
  })
  // Already filtered to active by the endpoint, unlike programmes.
  const { data: serviceTypes = [] } = useQuery({
    queryKey: ["service-types"],
    queryFn: ({ signal }) => fetchServiceTypes(signal),
    retry: false,
    meta: { source: "fetchServiceTypes" },
  })
  const saveable = changed && canSave(state) && !nameTaken

  // The whole template is sent: a save replaces it, so what the body does not
  // carry it would lose.
  const save = useMutation({
    mutationFn: (sent: TemplateContent) =>
      updateSessionTemplate(sessionTemplate.id, toUpdate(sent, state.revision)),
    onSuccess: (saved, sent) => {
      dispatch({ type: "saved", sent, template: saved })
      queryClient.setQueryData(["session-template", sessionTemplate.id], saved)
    },
    onError: (err) => {
      // A 4xx carries a reason the administrator can act on and is shown as
      // sent below; only an unexpected failure is a fault worth recording.
      if (!isExpectedApiError(err)) {
        trackError(err, { source: "updateSessionTemplate" })
      }
    },
  })
  const saving = save.isPending
  const isChangedElsewhere =
    save.error instanceof ApiResponseError &&
    save.error.code === SessionTemplateErrorCode.SESSION_TEMPLATE_CHANGED
  const [isReloadFailed, setIsReloadFailed] = useState(false)

  /**
   * Replaces what is edited with the template as it is stored now, after a
   * save was refused for someone else's: what was changed here is dropped, as
   * the message says. A reload that fails keeps the page as it is, and says so.
   */
  async function reload() {
    setIsReloadFailed(false)
    try {
      const stored = await queryClient.fetchQuery({
        queryKey: ["session-template", sessionTemplate.id],
        queryFn: ({ signal }) =>
          fetchSessionTemplate(sessionTemplate.id, signal),
        staleTime: 0,
        // As the page's own read: an administrator at a desk is told.
        retry: false,
      })
      dispatch({ type: "reloaded", template: stored })
      save.reset()
    } catch (err) {
      trackError(err, { source: "fetchSessionTemplate" })
      setIsReloadFailed(true)
    }
  }

  const saveError = save.error ? (
    isExpectedApiError(save.error) ? (
      <SaveRefusal error={save.error} />
    ) : (
      "Could not save the session template. Please try again."
    )
  ) : null
  // The headings follow the name as it is typed, as when it was set up.
  const shownName = content.name.trim() || sessionTemplate.name
  const programmeOptions = pickerOptions(
    programs,
    sessionTemplate.programs,
    content.programIds,
  )

  return (
    <>
      <SessionTemplateBuilder
        name={shownName}
        version={sessionTemplate.version}
        headerAction={
          <AdminButton variant="outline" onClick={() => setPreviewing(true)}>
            <EyeIcon className={styles.previewIcon} aria-hidden="true" />
            Preview
          </AdminButton>
        }
        notice={
          isChangedElsewhere ? (
            <div className={styles.conflict}>
              <ErrorCallout>
                Someone has saved this template since you opened it. Reload it
                to see their changes; what you&rsquo;ve changed here won&rsquo;t
                be kept.
              </ErrorCallout>
              {isReloadFailed && (
                <ErrorCallout>
                  Couldn&rsquo;t reload the template. Please try again.
                </ErrorCallout>
              )}
              <AdminButton variant="outline" onClick={() => void reload()}>
                Reload template
              </AdminButton>
            </div>
          ) : (
            saveError && <ErrorCallout>{saveError}</ErrorCallout>
          )
        }
        details={
          <>
            <SessionTemplateNameFields
              name={content.name}
              description={content.description}
              onNameChange={(name) => dispatch({ type: "renamed", name })}
              onDescriptionChange={(description) =>
                dispatch({ type: "described", description })
              }
              nameTaken={nameTaken}
            />
            <MultiSelectField
              label="Programmes"
              options={programmeOptions}
              selectedIds={content.programIds}
              onChange={(ids) => dispatch({ type: "programsChanged", ids })}
              placeholder="Search programmes"
            />
            <MultiSelectField
              label="Service types"
              options={pickerOptions(
                serviceTypes,
                sessionTemplate.serviceTypes,
                content.serviceTypeIds,
              )}
              selectedIds={content.serviceTypeIds}
              onChange={(ids) => dispatch({ type: "serviceTypesChanged", ids })}
              placeholder="Search service types"
            />
          </>
        }
        actions={
          <>
            <AdminButton
              variant="outline"
              ref={cancelRef}
              // Asked only when something would be lost.
              onClick={() =>
                changed ? setLeaving(true) : navigate(paths.sessionTemplates)
              }
            >
              Cancel
            </AdminButton>
            <AdminButton
              // aria-disabled leaves the button clickable, so the guard is here.
              onClick={() => saveable && !saving && save.mutate(content)}
              ariaDisabled={!saveable || saving}
            >
              Save template
            </AdminButton>
          </>
        }
        unsaved={changed}
        onAddModule={(module) =>
          dispatch({ type: "added", module, at: content.modules.length })
        }
        placedModuleIds={content.modules.map((module) => module.id)}
      >
        {content.modules.length === 0 ? (
          <EmptyCanvas>
            Add a module from the library to start building the template.
          </EmptyCanvas>
        ) : (
          <ul aria-label="Template modules" className={styles.modules}>
            {content.modules.map((module, index) => (
              <PlacedModuleCard
                key={module.id}
                module={module}
                first={index === 0}
                last={index === content.modules.length - 1}
                onMove={(by) => dispatch({ type: "moved", index, by })}
                onRemove={() => dispatch({ type: "removed", id: module.id })}
              />
            ))}
          </ul>
        )}
      </SessionTemplateBuilder>

      <DiscardChangesDialog
        open={leaving}
        onKeepEditing={() => {
          setLeaving(false)
          // Back where the question came from, so the keyboard does not start
          // over at the top of the page.
          cancelRef.current?.focus()
        }}
        onDiscard={() => navigate(paths.sessionTemplates)}
      />
      {previewing && (
        <SessionTemplatePreviewDialog
          onClose={() => setPreviewing(false)}
          name={shownName}
          description={content.description}
          programmes={content.programIds.map(
            (id) => programmeOptions.find((option) => option.id === id)!.name,
          )}
          modules={content.modules}
        />
      )}
    </>
  )
}

/**
 * What a picker offers on a saved template: what may be added, and whatever
 * the template holds and still has selected. The template's own copy names
 * them, so its chips show even when the list did not load, and one ICIS has
 * retired since is marked so. Taking one of those off takes it out of the
 * offer, so a retired one cannot be added back.
 */
function pickerOptions(
  offered: { id: string; name: string }[],
  held: { id: string; name: string; active: boolean }[],
  selectedIds: string[],
) {
  const holding = held
    .filter((item) => selectedIds.includes(item.id))
    .map((item) => ({
      id: item.id,
      name: item.active ? item.name : `${item.name} (retired)`,
    }))
  return [...offered, ...holding]
}
